import React, { useState, useEffect, useRef, lazy, Suspense } from "react";
import useExceptionHandler from "hooks/useExceptionHandler";
import useMessage from "hooks/useMessage";
import usePantallaEdit from "hooks/usePantallaEdit";
import { useRequestTracker } from "hooks/useResquestTracker";
import { CondicionPagoManager } from "managers";
import { GridView, SvgComponent } from "components";
import IconButton from "components/IconButton/IconButton";
import ToolTip from "components/ToolTip/ToolTip";
import ListHeader from "components/PantallaList/Header/ListHeader";
import { Simplebar } from "components/Simplebar/Simplebar";
import usePaginationValuesWhere from "hooks/usePaginationValuesWhere";
import Select from "react-select";
import { CondicionPago } from "models/ECF/CondicionPago";
import { DEFAULT_PAGINATION_VALUES } from "utils";
import SuspenseECF from "components/Suspense/SuspenseECF";
const Exportar = lazy(() => import("components/Exportar/Exportar"));
const CondicionPagoEdit = lazy(() => import("./CondicionPagoEdit"));

const CondicionPagoList = ({ showLoading, hideLoading, hideTitle, isActive }) => {
    const [where, setWhere] = useState({});
    const [pagination, setPagination] = useState(DEFAULT_PAGINATION_VALUES);
    const [list, setList] = useState([]);
    const { setPaginationValuesWhere } = usePaginationValuesWhere();
    const { handleException } = useExceptionHandler();
    const [showCanvas, setShowCanvas] = useState(false);
    const [condicionPago, setCondicionPagoState] = useState();
    const { Message, cerrarAlertas } = useMessage();
    const ref = useRef(null);
    const [showExportar, setShowExportar] = useState(false);
    const { trackRequest } = useRequestTracker();
    const ESTATUS_LIST = [
        { value: "", label: "Todos" },
        ...CondicionPago.ESTATUS_LIST,
    ];
    
    const {
        setEntidad: setCondicionPago,
        setEstadoCargado,
        setHayCambios,
    } = usePantallaEdit(setCondicionPagoState);

    useEffect(() => {
        if (isActive) {
            buscar();
        }
        return () => {
            cerrarAlertas();
            hideLoading();
        };
    }, [where, isActive]);

    useEffect(() => {
        if (condicionPago != undefined) {
            setShowCanvas(true);
        }
    }, [condicionPago]);

    const buscar = () => {
        const whereWithPaginationValues = { ...where, ...pagination };

        
        trackRequest({
            executeRequest: (signal) =>
                CondicionPagoManager.listado(whereWithPaginationValues, {
                    signal,
                }),
            onSuccess: (res) => {
                setPaginationValuesWhere(res.Headers, pagination, setPagination);
                setList(res.Data);
            },
            onError: (error) => handleException(error),
            onFinally: () => hideLoading(),
        });
    };

    const onPageIndexChanging = (pageNumber) => {
        setPagination((prevstate) => {
            return { ...prevstate, IndiceDePagina: pageNumber - 1 };
        });
        setWhere({ ...where });
    };

    const handleClick = (id) => {
        setEstadoCargado(false);
        if (id) {
            CondicionPagoManager.buscarPorId(id)
                .then((data) => {
                    setCondicionPagoState(data);
                })
                .catch((e) => {
                    handleException(e);
                })
                .finally(() => {
                    hideLoading();
                    setEstadoCargado(true);
                });
        } else {
            setCondicionPagoState({});
            setEstadoCargado(true);
        }
    };

    const grabar = () => {
        ref.current?.showLoading(true);

        if (!condicionPago.IdSec) {
            CondicionPagoManager.crear(condicionPago)
                .then((res) => {
                    Message({ message: `Grabado Exitosamente` });
                    buscar();
                    setCondicionPagoState(res);
                    setShowCanvas(false);
                })
                .catch((e) => {
                    handleException(e);
                })
                .finally(() => {
                    ref.current?.showLoading(false);
                    setHayCambios(false);
                });
        } else {
            CondicionPagoManager.actualizar(condicionPago)
                .then(() => {
                    buscar();
                    Message({ message: `Actualizado Exitosamente` });
                    setShowCanvas(false);
                })
                .catch((e) => {
                    handleException(e);
                })
                .finally(() => {
                    ref.current?.showLoading(false);
                    setHayCambios(false);
                });
        }
    };

    const condicionPagoEdit = (
        <Simplebar
            ref={ref}
            backdrop={"static"}
            confirmBack
            placement={"end"}
            portal={false}
            visible={showCanvas}
            setShow={(e) => {
                setShowCanvas(e);
            }}
            hideCallback={() => {
                setEstadoCargado(false);
            }}
            useLoading={true}
            title={
                condicionPago?.IdSec || false ? "Actualizar Condición de Pago" : "Crear Condición de Pago"
            }
            btnLeft={{
                show: true,
                message: "Grabar",
                isComponent: true,
                size: "45px",
                height: "24px",
                with: "24px",
                iconName: "save",
                isCenter: true,
                func: grabar,
            }}
            btnRight={{
                show: false,
                label: "Grabar",
                func: () => { },
            }}
        >
            <CondicionPagoEdit condicionPago={condicionPago} setCondicionPago={setCondicionPagoState} />
        </Simplebar>
    );

    const openExportar = () => {
        setShowExportar(true);
    };

    return (
        <div>
            <div style={{ paddingRight: "6px" }}>
                <ListHeader
                    titulo={hideTitle ? "" : "Condición de Pago "}
                    crearNuevo={() => handleClick()}
                    top="0px"
                    exportar={openExportar}
                ></ListHeader>
            </div>
            {showCanvas && (
                <Suspense fallback={<SuspenseECF />}>{condicionPagoEdit}</Suspense>
            )}
            <>
                <GridView
                    pagination={pagination}
                    setPagination={setPagination}
                    allowPaging={pagination.EsPaginable}
                    pageSize={pagination.CantidadPorPagina}
                    pageIndex={pagination.IndiceDePagina}
                    lastPageNumber={pagination.PaginasTotal}
                    dataSource={list}
                    onPageIndexChanging={onPageIndexChanging}
                    where={where}
                    setWhere={setWhere}
                    height="70vh"
                >
                    <GridView.Column
                        isSticky
                        bodyRenderer={(row) => {
                            return (
                                <>
                                    <div className="float-right">
                                        <IconButton onClick={() => handleClick(row.IdSec)}>
                                            <ToolTip message="Editar">
                                                <div>
                                                    <SvgComponent
                                                        iconName="pencil"
                                                        height="25"
                                                        width="25"
                                                    />
                                                </div>
                                            </ToolTip>
                                        </IconButton>
                                    </div>
                                </>
                            );
                        }}
                    />
                    <GridView.Column
                        headerText="Id"
                        dataField="IdSec"
                        sortExpression="IdSec"
                        width="100px"
                    />
                    <GridView.Column
                        headerText="Descripción"
                        dataField="Descripcion"
                        sortExpression="Descripcion"
                    />
                    <GridView.Column
                        headerText="Estatus"
                        dataField="Estatus"
                        sortExpression="Estatus"
                        filterRenderer={() => {
                            return (
                                <Select
                                    className="react-select primary"
                                    options={ESTATUS_LIST}
                                    value={ESTATUS_LIST.filter(function (option) {
                                        return (
                                            option.value ===
                                            (where.Estatus ? where.Estatus : ESTATUS_LIST[0].value)
                                        );
                                    })}
                                    name="Estatus"
                                    onChange={(e) =>
                                        setWhere({
                                            ...where,
                                            Estatus: e.value,
                                        })
                                    }
                                ></Select>
                            );
                        }}
                    />
                </GridView>
            </>
        </div>
    );
};

export default CondicionPagoList;
